import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { getContentfulData, contentfulFormConfig, getData } from './contentful';
import { replace, getPathVariables } from './misc';
import { useLocalizedStringsContext } from './context/LocalizedStringsContext';
import { useLocaleContext } from './context/LocaleContext';
import request from './utils/request';
import Form from './form';
import Dashboard from './dashboard';

const App = ({ history, location: { pathname, search } }) => {
  const {
    role: { name: roleId }
  } = request.getProfile();
  const localizedStrings = useLocalizedStringsContext();
  const {
    USERS: {
      FORM: {
        MESSAGE: { LOADING }
      }
    }
  } = localizedStrings;

  const [categories, setCategories] = useState(null);
  const [, categoryName, formName] = getPathVariables(pathname);
  const locale = useLocaleContext();
  const { orderId, step } = queryString.parse(search);
  const config = { ...contentfulFormConfig, locale };

  const categoryList = useMemo(
    () =>
      categories &&
      categories.reduce((acc, catGroup) => {
        const { categoryGroupItems } = catGroup;
        return [...acc, ...(categoryGroupItems || [])];
      }, []),
    [categories]
  );

  const findItem = (items, name) =>
    items.filter(
      item => replace(item.fields.name).toLowerCase() === name.toLowerCase()
    );

  const normalizeCategoryGroup = categoryGroup =>
    (categoryGroup || []).reduce((acc, group) => {
      const {
        fields: { categoryGroupTitle, categoryGroupItems } = {}
      } = group || {};
      const visibleCategories = (categoryGroupItems || []).filter(
        ({ fields: { availableForRole } = {} }) =>
          availableForRole && availableForRole.includes(roleId)
      );

      const shouldRenderGroup =
        visibleCategories.length > 0 || categoryGroupTitle === 'Other';

      return [
        ...acc,
        ...(shouldRenderGroup
          ? [
              {
                categoryGroupTitle,
                categoryGroupItems: visibleCategories
              }
            ]
          : [])
      ];
    }, []);

  useEffect(() => {
    getContentfulData(config).then(res => {
      const { formCategoryGroup } = getData(res);
      const normalizedCategoryGroup = normalizeCategoryGroup(formCategoryGroup);
      setCategories(normalizedCategoryGroup);
    });
  }, [locale]);

  if (!categories) return <p>{LOADING}</p>;
  if (categoryName) {
    const currentCategory = findItem(categoryList, categoryName);
    if (currentCategory.length) {
      const {
        fields: { forms }
      } = currentCategory[0];
      const currentForm = findItem(forms, formName);

      if (currentForm.length === 1) {
        return (
          <>
            <Form
              data={currentForm[0]}
              orderId={orderId}
              initialStep={step}
              history={history}
            />
          </>
        );
      }
    } else {
      history.push('/');
    }
  }

  return (
    <>
      <Dashboard categories={categories} locale={locale} history={history} />
    </>
  );
};

App.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }).isRequired
};

export default App;
